import { useState, useEffect } from "react";
import Close from "@mui/icons-material/Close";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import DownloadIcon from "@mui/icons-material/Download";
import { Switch, CircularProgress } from "@mui/material";
import "./bulk-upload.css";

export function DetailSidePanel({
  isOpen,
  onClose,
  record,
  onAccordionSelect,
  accordionSelected,
  tabId,
  onMoveRecord,
  onVendorSearch,
  isCountryEligibleForVendorSearch,
}) {
  const [activeTab, setActiveTab] = useState("hcp");
  const [activeSourceByTab, setActiveSourceByTab] = useState({
    hcp: "eph",
    hco: "eph",
  });
  const [expandedAccordions, setExpandedAccordions] = useState(new Set());
  const [hcpPage, setHcpPage] = useState(1);
  const [hcoPage, setHcoPage] = useState(1);
  const pageSize = 10; // Fixed page size

  const activeSourceTab = activeSourceByTab[activeTab] || "eph";

  // Reset accordion state when record changes
  useEffect(() => {
    if (record) {
      setExpandedAccordions(new Set());
      setHcpPage(1);
      setHcoPage(1);
      setActiveTab("hcp");

      const hasEntityInput = (entity) =>
        !!entity &&
        typeof entity === "object" &&
        Object.keys(entity).length > 0;

      const hasInputHcp = hasEntityInput(record?.inputEntities?.hcp);
      const hasInputHco = hasEntityInput(record?.inputEntities?.hco);
      const hasInputParentHco = hasEntityInput(
        record?.inputEntities?.parentHco,
      );

      const isSingleHcoLayout =
        hasInputHco && !hasInputHcp && !hasInputParentHco;
      const isHcoParentLayout =
        hasInputHco && hasInputParentHco && !hasInputHcp;
      const isHcpParentLayout =
        hasInputHcp && hasInputParentHco && !hasInputHco;

      const leftEntityType =
        isSingleHcoLayout || isHcoParentLayout ? "hco" : "hcp";
      const rightEntityType =
        isHcoParentLayout || isHcpParentLayout ? "parent_hco" : "hco";

      const getSourceForEntity = (entityType) => {
        const rawSource =
          entityType === "hcp"
            ? record?.hcpSource || record?.hcp_source
            : entityType === "hco"
              ? record?.hcoSource || record?.hco_source
              : record?.parentHcoSource || record?.parent_hco_source;

        return String(rawSource || "")
          .trim()
          .toLowerCase() === "vendor"
          ? "vendor"
          : "eph";
      };

      setActiveSourceByTab({
        hcp: getSourceForEntity(leftEntityType),
        hco: getSourceForEntity(rightEntityType),
      });
    }
  }, [record?.id]);

  useEffect(() => {
    if (record?.vendorStatusView) {
      setActiveSourceByTab((prev) => ({
        ...prev,
        hcp: prev?.hcp || record.vendorStatusView,
        hco: prev?.hco || record.vendorStatusView,
      }));
    }
  }, [record?.vendorStatusView]);

  const hasEntityInputForLayout = (entity) =>
    !!entity && typeof entity === "object" && Object.keys(entity).length > 0;
  const hasInputHcpForLayout = hasEntityInputForLayout(
    record?.inputEntities?.hcp,
  );
  const hasInputHcoForLayout = hasEntityInputForLayout(
    record?.inputEntities?.hco,
  );
  const hasInputParentHcoForLayout = hasEntityInputForLayout(
    record?.inputEntities?.parentHco,
  );
  const showRightTabForLayout = !(
    (hasInputHcpForLayout &&
      !hasInputHcoForLayout &&
      !hasInputParentHcoForLayout) ||
    (hasInputHcoForLayout &&
      !hasInputHcpForLayout &&
      !hasInputParentHcoForLayout)
  );

  useEffect(() => {
    if (!record) {
      return;
    }
    if (!showRightTabForLayout && activeTab === "hco") {
      setActiveTab("hcp");
    }
  }, [activeTab, record, showRightTabForLayout]);

  const getAccordionDataBySource = (sourceType) =>
    sourceType === "vendor"
      ? record?.vendorAccordionData || {}
      : record?.ephAccordionData || record?.accordionData || {};

  const sourceAccordionData = getAccordionDataBySource(activeSourceTab);
  const leftTabSourceType = activeSourceByTab.hcp || "eph";
  const rightTabSourceType = activeSourceByTab.hco || "eph";
  const leftTabSourceAccordionData =
    getAccordionDataBySource(leftTabSourceType);
  const rightTabSourceAccordionData =
    getAccordionDataBySource(rightTabSourceType);

  if (!record) return null;

  const toggleAccordion = (id) => {
    const newExpanded = new Set(expandedAccordions);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedAccordions(newExpanded);
  };

  const handleRadioSelect = (id, type) => {
    if (onAccordionSelect && record) {
      onAccordionSelect(record.id, id, type);
    }
  };

  const hasAccordion = record.hasAccordion;

  // Layout detection based on input entities
  const hasEntityInput = (entity) =>
    !!entity && typeof entity === "object" && Object.keys(entity).length > 0;
  const hasInputHcp = hasEntityInput(record?.inputEntities?.hcp);
  const hasInputHco = hasEntityInput(record?.inputEntities?.hco);
  const hasInputParentHco = hasEntityInput(record?.inputEntities?.parentHco);
  const isSingleHcpLayout = hasInputHcp && !hasInputHco && !hasInputParentHco;
  const isSingleHcoLayout = hasInputHco && !hasInputHcp && !hasInputParentHco;
  const useHcoOnLeft = isSingleHcoLayout;
  const isHcoParentLayout = hasInputHco && hasInputParentHco && !hasInputHcp;
  const isHcpParentLayout = hasInputHcp && hasInputParentHco && !hasInputHco;
  const showRightTab = !(isSingleHcpLayout || isSingleHcoLayout);
  const useParentHcoOnRight = isHcoParentLayout || isHcpParentLayout;

  const leftTabLabel = isHcoParentLayout || useHcoOnLeft ? "HCO" : "HCP";
  const rightTabLabel = useParentHcoOnRight ? "Parent HCO" : "HCO";
  const leftSelectionType = isHcoParentLayout || useHcoOnLeft ? "hco" : "hcp";
  const rightSelectionType = useParentHcoOnRight ? "parentHco" : "hco";

  // For tab display counts - use each tab's own source toggle state.
  const leftTabDisplayData =
    isHcoParentLayout || useHcoOnLeft
      ? leftTabSourceAccordionData?.hco || []
      : leftTabSourceAccordionData?.hcp || [];
  const rightTabDisplayData = useParentHcoOnRight
    ? rightTabSourceAccordionData?.parentHco || []
    : rightTabSourceAccordionData?.hco || [];

  // For actual rendering - use current active tab's source data
  const hcpData = sourceAccordionData?.hcp || [];
  const hcoData = sourceAccordionData?.hco || [];
  const parentHcoData = sourceAccordionData?.parentHco || [];
  const leftTabData = isHcoParentLayout || useHcoOnLeft ? hcoData : hcpData;
  const rightTabData = useParentHcoOnRight ? parentHcoData : hcoData;

  const selectedInHcp =
    !!accordionSelected?.[`${record.id}-${leftSelectionType}`];
  const selectedInHco =
    !!accordionSelected?.[`${record.id}-${rightSelectionType}`];
  const selectedHcpId =
    accordionSelected?.[`${record.id}-${leftSelectionType}`];
  const selectedHcoId =
    accordionSelected?.[`${record.id}-${rightSelectionType}`];
  const selectedParentHcoId = accordionSelected?.[`${record.id}-parentHco`];
  const selectedHcpRecord =
    leftTabData.find((item) => item.id === selectedHcpId) || null;
  const selectedHcoRecord =
    rightTabData.find((item) => item.id === selectedHcoId) || null;
  const selectedParentHcoRecord =
    parentHcoData.find((item) => item.id === selectedParentHcoId) || null;
  const effectiveSelectedHcpRecord = selectedHcpRecord;
  const effectiveSelectedHcoRecord = selectedHcoRecord;
  const effectiveSelectedParentHcoRecord = selectedParentHcoRecord;
  const showSelectedInHcp = selectedInHcp;
  const showSelectedInHco = selectedInHco;
  const leftSourceValue = String(
    isHcoParentLayout || useHcoOnLeft
      ? record?.hcoSource || record?.hco_source || ""
      : record?.hcpSource || record?.hcp_source || "",
  )
    .trim()
    .toLowerCase();
  const rightSourceValue = String(
    useParentHcoOnRight
      ? record?.parentHcoSource || record?.parent_hco_source || ""
      : record?.hcoSource || record?.hco_source || "",
  )
    .trim()
    .toLowerCase();
  const isVendorRightPanelData =
    tabId === "vendor" ||
    activeSourceTab === "vendor" ||
    rightSourceValue === "vendor";

  const canSearchLeftFromEph =
    tabId === "eph" && leftTabData.length > 0 && leftSourceValue === "eph";
  const canSearchRightFromEph =
    showRightTab &&
    tabId === "eph" &&
    rightTabData.length > 0 &&
    rightSourceValue === "eph";

  const vendorSearchStatus = record?.vendorSearchStatus || {};
  const getVendorStatusForEntityType = (entityType) => {
    if (entityType === "hcp") {
      return vendorSearchStatus?.hcp_vendor_search_status || "";
    }
    if (entityType === "parent_hco") {
      return vendorSearchStatus?.parent_hco_vendor_search_status || "";
    }
    return vendorSearchStatus?.hco_vendor_search_status || "";
  };
  const getVendorStatusLabel = (status) => {
    const normalizedStatus = String(status || "")
      .trim()
      .toUpperCase();

    if (normalizedStatus === "SUBMITTED_IN_QUEUE") {
      return "Submitted in Queue";
    }
    if (normalizedStatus === "IN_PROGRESS") {
      return "In Progress";
    }
    if (normalizedStatus === "COMPLETED") {
      return "Completed";
    }

    return "Submitted in Queue";
  };
  const leftVendorEntityType =
    leftSelectionType === "parentHco" ? "parent_hco" : leftSelectionType;
  const rightVendorEntityType =
    rightSelectionType === "parentHco" ? "parent_hco" : rightSelectionType;
  const activeTabVendorEntityType =
    activeTab === "hcp" ? leftVendorEntityType : rightVendorEntityType;
  const optedVendorEntityTypes = [
    vendorSearchStatus?.opted_for_hcp_vendor_search === true ? "hcp" : null,
    vendorSearchStatus?.opted_for_hco_vendor_search === true ? "hco" : null,
    vendorSearchStatus?.opted_for_parent_hco_vendor_search === true
      ? "parent_hco"
      : null,
  ].filter(Boolean);
  const hasAnyVendorOptIn = optedVendorEntityTypes.length > 0;
  const leftVendorSearchAlreadyOpted =
    optedVendorEntityTypes.includes(leftVendorEntityType);
  const rightVendorSearchAlreadyOpted = optedVendorEntityTypes.includes(
    rightVendorEntityType,
  );

  const baseShouldShowVendorSearchBoth =
    showRightTab && !selectedInHcp && !selectedInHco && canSearchLeftFromEph;
  const baseShouldShowVendorSearchHcp = !showRightTab
    ? !selectedInHcp && canSearchLeftFromEph
    : (!selectedInHcp && selectedInHco && canSearchLeftFromEph) ||
      (!selectedInHcp &&
        !selectedInHco &&
        canSearchLeftFromEph &&
        !canSearchRightFromEph);
  const baseShouldShowVendorSearchHco = !showRightTab
    ? false
    : (selectedInHcp && !selectedInHco && canSearchRightFromEph) ||
      (!selectedInHcp &&
        !selectedInHco &&
        !canSearchLeftFromEph &&
        canSearchRightFromEph);

  const shouldShowVendorSearchBoth =
    isCountryEligibleForVendorSearch &&
    !hasAnyVendorOptIn &&
    baseShouldShowVendorSearchBoth;
  const shouldShowVendorSearchHcp =
    isCountryEligibleForVendorSearch &&
    !leftVendorSearchAlreadyOpted &&
    (baseShouldShowVendorSearchHcp ||
      (hasAnyVendorOptIn &&
        activeTab === "hcp" &&
        canSearchLeftFromEph &&
        !selectedInHcp));
  const shouldShowVendorSearchHco =
    isCountryEligibleForVendorSearch &&
    !rightVendorSearchAlreadyOpted &&
    (baseShouldShowVendorSearchHco ||
      (hasAnyVendorOptIn &&
        activeTab === "hco" &&
        canSearchRightFromEph &&
        !selectedInHco));

  const showVendorSearchOnHcpTab =
    activeTab === "hcp" && shouldShowVendorSearchHcp;
  const showVendorSearchOnHcoTab =
    showRightTab && activeTab === "hco" && shouldShowVendorSearchHco;
  const requestedTypes = [];
  if (shouldShowVendorSearchBoth) {
    requestedTypes.push(leftVendorEntityType, rightVendorEntityType);
  } else {
    if (showVendorSearchOnHcpTab) {
      requestedTypes.push(leftVendorEntityType);
    }
    if (showVendorSearchOnHcoTab) {
      requestedTypes.push(rightVendorEntityType);
    }
  }
  const normalizedRequestedTypes = [...new Set(requestedTypes)];
  const vendorSearchLabel = shouldShowVendorSearchBoth
    ? "Vendor Search Both"
    : showVendorSearchOnHcpTab
      ? `Vendor Search ${leftTabLabel}`
      : showVendorSearchOnHcoTab
        ? `Vendor Search ${rightTabLabel}`
        : null;
  const shouldShowVendorToggle = optedVendorEntityTypes.includes(
    activeTabVendorEntityType,
  );
  const statusDerivedRequestedTypes = [
    vendorSearchStatus?.opted_for_hcp_vendor_search === true ? "hcp" : null,
    vendorSearchStatus?.opted_for_hco_vendor_search === true ? "hco" : null,
    vendorSearchStatus?.opted_for_parent_hco_vendor_search === true
      ? "parent_hco"
      : null,
  ].filter(Boolean);
  const effectiveRequestedTypes =
    Array.isArray(record?.vendorSearchRequestTypes) &&
    record.vendorSearchRequestTypes.length > 0
      ? record.vendorSearchRequestTypes
      : statusDerivedRequestedTypes;
  const showHcpVendorStatus =
    effectiveRequestedTypes.length > 0
      ? effectiveRequestedTypes.includes("hcp")
      : !!vendorSearchStatus?.opted_for_hcp_vendor_search;
  const showHcoVendorStatus =
    effectiveRequestedTypes.length > 0
      ? effectiveRequestedTypes.includes("hco")
      : !!vendorSearchStatus?.opted_for_hco_vendor_search;
  const showParentHcoVendorStatus =
    effectiveRequestedTypes.length > 0
      ? effectiveRequestedTypes.includes("parent_hco")
      : !!vendorSearchStatus?.opted_for_parent_hco_vendor_search;
  const hasSwitchForActiveTab =
    shouldShowVendorToggle &&
    effectiveRequestedTypes.includes(activeTabVendorEntityType);
  const shouldHideVendorSearchButton = hasSwitchForActiveTab;
  const showVendorSearchButton =
    isCountryEligibleForVendorSearch &&
    !!vendorSearchLabel &&
    !shouldHideVendorSearchButton;
  const vendorSearchClassName = shouldShowVendorSearchBoth
    ? "px-3 py-1 text-xs font-medium text-indigo-700 bg-white border border-indigo-300 rounded hover:bg-indigo-50 transition-colors"
    : showVendorSearchOnHcpTab
      ? "px-3 py-1 text-xs font-medium text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-100 transition-colors"
      : "px-3 py-1 text-xs font-medium text-green-700 bg-white border border-green-300 rounded hover:bg-green-100 transition-colors";

  const isVendorLoadingView =
    record?.vendorSearchInProgress && activeSourceTab === "vendor";
  const activeTabLoadingLabel =
    activeTab === "hcp" ? leftTabLabel : rightTabLabel;
  const activeTabVendorStatus =
    getVendorStatusForEntityType(activeTabVendorEntityType) ||
    "SUBMITTED_IN_QUEUE";
  const activeTabVendorStatusLabel = getVendorStatusLabel(
    activeTabVendorStatus,
  );
  const readByPath = (obj, path) => {
    if (!obj || !path) {
      return undefined;
    }
    return path
      .split(".")
      .reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
  };

  const getEntityAddress = (entityRecord) => {
    if (!entityRecord) {
      return "N/A";
    }

    const backendPayload = entityRecord?.backendPayload;
    const vendorAddresses = backendPayload?.entity?.addresses__v;
    const primaryVendorAddress = Array.isArray(vendorAddresses)
      ? vendorAddresses.find(
          (address) =>
            String(address?.primary_cda__v || "").toUpperCase() === "Y",
        ) ||
        vendorAddresses.find(
          (address) => Number(address?.address_ordinal__v) === 1,
        ) ||
        vendorAddresses[0]
      : null;

    return (
      entityRecord?.address ||
      backendPayload?.Address ||
      backendPayload?.address ||
      backendPayload?.hcp_adrs_line_1 ||
      backendPayload?.hco_adrs_line_1 ||
      backendPayload?.rel_hco_adrs_line_1 ||
      primaryVendorAddress?.formatted_address__v ||
      primaryVendorAddress?.address_line_1__v ||
      primaryVendorAddress?.street_address_1__v ||
      primaryVendorAddress?.delivery_address__v ||
      backendPayload?.entity?.street_address_1__v ||
      "N/A"
    );
  };

  const getEntityCity = (entityRecord) => {
    if (!entityRecord) {
      return "N/A";
    }

    const backendPayload = entityRecord?.backendPayload;
    const vendorAddresses = backendPayload?.entity?.addresses__v;
    const primaryVendorAddress = Array.isArray(vendorAddresses)
      ? vendorAddresses.find(
          (address) =>
            String(address?.primary_cda__v || "").toUpperCase() === "Y",
        ) ||
        vendorAddresses.find(
          (address) => Number(address?.address_ordinal__v) === 1,
        ) ||
        vendorAddresses[0]
      : null;

    return (
      entityRecord?.city ||
      backendPayload?.city ||
      backendPayload?.hcp_adrs_city ||
      backendPayload?.hco_adrs_city ||
      backendPayload?.rel_hco_adrs_city ||
      primaryVendorAddress?.city_cda__v ||
      primaryVendorAddress?.locality__v ||
      primaryVendorAddress?.city_intl__v ||
      backendPayload?.entity?.city_cda__v ||
      "N/A"
    );
  };

  const getPanelIdMeta = (entityRecord, entityType, sourceValue) => {
    const normalizedSource = String(
      entityRecord?.sourceType || sourceValue || "",
    )
      .trim()
      .toLowerCase();

    if (normalizedSource === "vendor") {
      const isOpenDataVendorPayload =
        !!readByPath(entityRecord, "backendPayload.entity") ||
        !!readByPath(entityRecord, "entity");
      const vendorEntityId =
        readByPath(entityRecord, "backendPayload.entityId") ||
        readByPath(entityRecord, "entityId") ||
        readByPath(entityRecord, "backendPayload.entity.vid__v") ||
        readByPath(entityRecord, "backendPayload.entity.veevaid__v") ||
        readByPath(entityRecord, "backendPayload.onekeyEid") ||
        readByPath(entityRecord, "onekeyEid");

      if (entityType === "hcp") {
        const individualEid =
          readByPath(entityRecord, "backendPayload.individual.individualEid") ||
          readByPath(entityRecord, "individual.individualEid") ||
          readByPath(entityRecord, "backendPayload.entity.entityId") ||
          readByPath(entityRecord, "backendPayload.entity.vid__v") ||
          readByPath(entityRecord, "backendPayload.entity.veevaid__v") ||
          entityRecord?.enterpriseId ||
          vendorEntityId ||
          "N/A";
        return {
          label: isOpenDataVendorPayload ? "Entity ID" : "OneKey ID",
          value: individualEid,
        };
      }

      const oneKeyId =
        readByPath(entityRecord, "backendPayload.onekeyEid") ||
        readByPath(entityRecord, "backendPayload.entityId") ||
        readByPath(entityRecord, "backendPayload.entity.vid__v") ||
        readByPath(entityRecord, "backendPayload.entity.veevaid__v") ||
        entityRecord?.onekeyEid ||
        entityRecord?.entityId ||
        entityRecord?.enterpriseId ||
        vendorEntityId ||
        "N/A";
      return {
        label: isOpenDataVendorPayload ? "Entity ID" : "OneKey ID",
        value: oneKeyId,
      };
    }

    return {
      label: "Enterprise ID",
      value: entityRecord?.enterpriseId || "N/A",
    };
  };

  const handleClearSelection = (type) => {
    if (onAccordionSelect && record) {
      onAccordionSelect(record.id, null, type);
    }
  };

  const hcpTotalPages = Math.ceil(leftTabData.length / pageSize);
  const hcoTotalPages = Math.ceil(rightTabData.length / pageSize);

  const paginatedHcpData = leftTabData.slice(
    (hcpPage - 1) * pageSize,
    hcpPage * pageSize,
  );
  const paginatedHcoData = rightTabData.slice(
    (hcoPage - 1) * pageSize,
    hcoPage * pageSize,
  );
  const hcpSubRecord = record.subRecords?.find((r) => r.type === "HCP") || null;
  const hcoSubRecord = record.subRecords?.find((r) => r.type === "HCO") || null;
  const useAccordionView = hasAccordion;
  const nonAccordionHcpRecord = effectiveSelectedHcpRecord || hcpSubRecord;
  const nonAccordionHcoRecord = effectiveSelectedHcoRecord || hcoSubRecord;
  const leftPanelIdMeta = getPanelIdMeta(
    effectiveSelectedHcpRecord || effectiveSelectedHcoRecord,
    leftSelectionType === "parentHco" ? "hco" : leftSelectionType,
    leftSourceValue,
  );
  const rightPanelIdMeta = getPanelIdMeta(
    effectiveSelectedHcoRecord || effectiveSelectedParentHcoRecord,
    rightSelectionType === "parentHco" ? "hco" : rightSelectionType,
    rightSourceValue,
  );

  const hasExportValue = (value) => {
    if (value === undefined || value === null) {
      return false;
    }

    if (typeof value === "object") {
      if (Array.isArray(value)) {
        return value.some(
          (entry) =>
            entry !== undefined &&
            entry !== null &&
            String(entry).trim() !== "",
        );
      }

      return Object.keys(value).length > 0;
    }

    return String(value).trim() !== "";
  };

  const escapeCsvValue = (value) => {
    const normalizedValue = value == null ? "" : String(value);
    if (
      normalizedValue.includes(",") ||
      normalizedValue.includes('"') ||
      normalizedValue.includes("\n")
    ) {
      return `"${normalizedValue.replace(/"/g, '""')}"`;
    }
    return normalizedValue;
  };

  const toTitleCaseHeader = (value) =>
    String(value || "")
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const getInputExportHeader = (key) => {
    if (
      String(key || "")
        .trim()
        .toLowerCase() === "input_row_number"
    ) {
      return "Row";
    }

    return `Input ${toTitleCaseHeader(key)}`;
  };

  const getExportResultObject = (item) => {
    const backendResult =
      item?.backendPayload && typeof item.backendPayload === "object"
        ? item.backendPayload
        : {};

    return {
      ...backendResult,
      ...item,
      entityId: item?.entityId || backendResult?.EntityId || item?.id || "",
      activeFlag: item?.activeFlag || backendResult?.ActiveFlag || "",
    };
  };

  const buildSingleRecordVendorDataset = (rowRecord, entityConfig) => {
    const excludedResultHeaders = new Set([
      "status",
      "source type",
      "enterprise id type",
      "entity id",
      "type",
      "enterprise id",
      "individual",
      "relevancy score",
      "is in contract",
      "is privacy law",
      "entity type",
      "cod base",
      "code base",
    ]);

    const inputEntity = rowRecord?.inputEntities?.[entityConfig.inputKey];
    const vendorResults = Array.isArray(
      rowRecord?.vendorAccordionData?.[entityConfig.resultKey],
    )
      ? rowRecord.vendorAccordionData[entityConfig.resultKey]
      : [];

    if (
      !inputEntity ||
      typeof inputEntity !== "object" ||
      Object.keys(inputEntity).length === 0 ||
      vendorResults.length === 0
    ) {
      return null;
    }

    const inputKeys = Object.keys(inputEntity).filter((key) =>
      hasExportValue(inputEntity?.[key]),
    );
    const inputRowNumberKey = inputKeys.find(
      (key) =>
        String(key || "")
          .trim()
          .toLowerCase() === "input_row_number",
    );
    const inputKeysWithoutRowNumber = inputKeys.filter(
      (key) => key !== inputRowNumberKey,
    );

    const resultKeySet = new Set();
    vendorResults.forEach((resultItem) => {
      const exportResult = getExportResultObject(resultItem);
      Object.keys(exportResult || {}).forEach((key) => {
        if (key === "id" || key === "backendPayload") {
          return;
        }
        resultKeySet.add(key);
      });
    });

    const resultKeys = Array.from(resultKeySet).filter((key) => {
      const normalized = String(key || "")
        .trim()
        .toLowerCase();
      const normalizedHeader = toTitleCaseHeader(key).toLowerCase();
      if (normalized === "workplace") {
        return false;
      }

      if (excludedResultHeaders.has(normalizedHeader)) {
        return false;
      }

      return vendorResults.some((resultItem) => {
        const exportResult = getExportResultObject(resultItem);
        return hasExportValue(exportResult?.[key]);
      });
    });

    const headers = [
      "Row",
      ...inputKeysWithoutRowNumber.map((key) => getInputExportHeader(key)),
      ...resultKeys.map((key) => toTitleCaseHeader(key)),
    ];

    const rows = vendorResults.map((resultItem, rowIndex) => {
      const exportResult = getExportResultObject(resultItem);
      return [
        inputRowNumberKey ? (inputEntity?.[inputRowNumberKey] ?? "") : "",
        ...inputKeysWithoutRowNumber.map((key) => {
          const value = inputEntity?.[key];
          if (value === undefined || value === null) {
            return "";
          }
          if (typeof value === "object") {
            try {
              return JSON.stringify(value);
            } catch (error) {
              return String(value);
            }
          }
          return value;
        }),
        ...resultKeys.map((key) => {
          const value = exportResult?.[key];
          if (value === undefined || value === null) {
            return "";
          }
          if (typeof value === "object") {
            try {
              return JSON.stringify(value);
            } catch (error) {
              return String(value);
            }
          }
          return value;
        }),
      ];
    });

    return { headers, rows };
  };

  const triggerCsvDownload = (headers, rows, fileName) => {
    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsvValue).join(","))
      .join("\n");
    const utf8Bom = "\uFEFF";

    const blob = new Blob([utf8Bom, csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  };

  const datasetConfigs = [
    {
      inputKey: "hcp",
      resultKey: "hcp",
      fileSuffix: "hcp",
      vendorType: "hcp",
      label: "Export Hcp Vendor Result",
    },
    {
      inputKey: "hco",
      resultKey: "hco",
      fileSuffix: "hco",
      vendorType: "hco",
      label: "Export Hco Vendor Result",
    },
    {
      inputKey: "parentHco",
      resultKey: "parentHco",
      fileSuffix: "parent_hco",
      vendorType: "parent_hco",
      label: "Export Parent Hco Vendor Result",
    },
  ];

  const getCanExportForDataset = (config) => {
    const requestedEntityTypes =
      Array.isArray(effectiveRequestedTypes) &&
      effectiveRequestedTypes.length > 0
        ? effectiveRequestedTypes
        : optedVendorEntityTypes;
    const isEntityRequested = requestedEntityTypes.includes(config.vendorType);

    const inputEntity = record?.inputEntities?.[config.inputKey];
    const hasInputEntity =
      !!inputEntity &&
      typeof inputEntity === "object" &&
      Object.values(inputEntity).some((value) => hasExportValue(value));

    const vendorResults = Array.isArray(
      record?.vendorAccordionData?.[config.resultKey],
    )
      ? record.vendorAccordionData[config.resultKey]
      : [];

    return (
      hasAnyVendorOptIn &&
      isEntityRequested &&
      hasInputEntity &&
      vendorResults.length > 0
    );
  };

  const exportableDatasetConfigs = datasetConfigs.filter((config) =>
    getCanExportForDataset(config),
  );
  const activeTabExportConfigs = exportableDatasetConfigs.filter(
    (config) => config.vendorType === activeTabVendorEntityType,
  );

  const handleExportVendorResultByType = (config) => {
    if (!getCanExportForDataset(config)) {
      return;
    }

    const safeRecordId = String(record?.pk || record?.id || "record").replace(
      /[^a-zA-Z0-9_-]/g,
      "_",
    );

    const dataset = buildSingleRecordVendorDataset(record, config);
    if (!dataset || !Array.isArray(dataset.rows) || dataset.rows.length === 0) {
      return;
    }

    triggerCsvDownload(
      dataset.headers,
      dataset.rows,
      `vendor_${config.fileSuffix}_record_${safeRecordId}.csv`,
    );
  };

  return (
    <>
      {/* Side Panel */}
      <div className={`bulk-ui-sidepanel ${isOpen ? "open" : ""}`}>
        {/* Header */}
        <div className="bulk-ui-sidepanel-header">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Details</h2>
            {/* <p className="text-xs text-gray-500 mt-0.5">
              {record.enterpriseId}
            </p> */}
          </div>
          <button onClick={onClose} className="close-btn-cs-panel">
            <Close className="" />
          </button>
        </div>

        {/* Tabs - Only show for accordion records */}
        {useAccordionView && (
          <div className="bulk-ui-sidepanel-tabs">
            <button
              onClick={() => setActiveTab("hcp")}
              className={activeTab === "hcp" ? "active" : ""}
            >
              {leftTabLabel} ({leftTabDisplayData.length})
              {showSelectedInHcp && (
                <span
                  className="ml-2 inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700"
                  style={{ marginLeft: "4px" }}
                >
                  <span>✓</span>
                  {/* <span>Reviewed</span> */}
                </span>
              )}
            </button>
            {showRightTab && (
              <button
                onClick={() => setActiveTab("hco")}
                className={activeTab === "hco" ? "active" : ""}
              >
                {rightTabLabel} ({rightTabDisplayData.length})
                {showSelectedInHco && (
                  <span
                    className="ml-2 inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700"
                    style={{ marginLeft: "4px" }}
                  >
                    <span>✓</span>
                    {/* <span>Reviewed</span> */}
                  </span>
                )}
              </button>
            )}
          </div>
        )}

        {/* Content */}
        <div className="bulk-ui-sidepanel-content">
          {useAccordionView ? (
            // Accordion view with multiple items
            <div className="space-y-2">
              {shouldShowVendorToggle && (
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-gray-50 px-2 py-1">
                    <span
                      className={`text-xs font-semibold ${
                        activeSourceTab === "eph"
                          ? "text-blue-700"
                          : "text-gray-600"
                      }`}
                    >
                      EPH
                    </span>
                    <Switch
                      size="small"
                      checked={activeSourceTab === "vendor"}
                      onChange={(event) =>
                        setActiveSourceByTab((prev) => ({
                          ...prev,
                          [activeTab]: event.target.checked ? "vendor" : "eph",
                        }))
                      }
                      inputProps={{ "aria-label": "eph vendor switch" }}
                    />
                    <span
                      className={`text-xs font-semibold ${
                        activeSourceTab === "vendor"
                          ? "text-green-700"
                          : "text-gray-600"
                      }`}
                    >
                      Vendor
                    </span>
                  </div>

                  {activeTabExportConfigs.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1">
                      {activeTabExportConfigs.map((config) => (
                        <button
                          key={config.fileSuffix}
                          type="button"
                          onClick={() => handleExportVendorResultByType(config)}
                          className="inline-flex items-center gap-1 rounded border border-green-300 bg-green-50 px-2 py-1 text-xs font-medium text-green-800 hover:bg-green-100 transition-colors"
                        >
                          <DownloadIcon sx={{ fontSize: 14 }} />
                          {config.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {showVendorSearchButton && (
                <div className="mb-2">
                  <button
                    type="button"
                    onClick={() =>
                      onVendorSearch?.(record.id, normalizedRequestedTypes)
                    }
                    className={vendorSearchClassName}
                  >
                    {vendorSearchLabel}
                  </button>
                </div>
              )}

              {isVendorLoadingView ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <CircularProgress
                    size={28}
                    sx={{ color: "rgb(204, 31, 31)", mb: 1 }}
                  />
                  <div className="text-sm font-semibold text-gray-700">
                    Loading vendor results...
                  </div>
                  <div className="mt-1 text-xs text-gray-600">
                    {`${activeTabLoadingLabel} status: ${activeTabVendorStatusLabel}`}
                  </div>
                </div>
              ) : (
                <>
                  {activeTab === "hcp" &&
                    effectiveSelectedHcpRecord?.enterpriseId && (
                      <div className="mb-2 flex items-center justify-between px-3 py-2 cs-pad-mb">
                        <div className="text-xs font-semibold text-blue-900 ">
                          ✓ Selected Result:{" "}
                          {
                            getPanelIdMeta(
                              effectiveSelectedHcpRecord,
                              "hcp",
                              leftSourceValue,
                            ).value
                          }
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            handleClearSelection(leftSelectionType)
                          }
                          className="clear-btn-cs text-xs font-medium text-blue-700 underline underline-offset-2 hover:text-blue-900"
                        >
                          Clear {leftTabLabel} Selection
                        </button>
                      </div>
                    )}

                  {showRightTab &&
                    activeTab === "hco" &&
                    effectiveSelectedHcoRecord?.enterpriseId && (
                      <div className="mb-2 flex items-center justify-between px-3 py-2 cs-pad-mb">
                        <div className="text-xs font-semibold text-green-900">
                          ✓ Selected Result:{" "}
                          {
                            getPanelIdMeta(
                              effectiveSelectedHcoRecord,
                              rightSelectionType === "parentHco"
                                ? "hco"
                                : rightSelectionType,
                              rightSourceValue,
                            ).value
                          }
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            handleClearSelection(rightSelectionType)
                          }
                          className="clear-btn-cs text-xs font-medium text-green-700 underline underline-offset-2 hover:text-green-900"
                        >
                          Clear {rightTabLabel} Selection
                        </button>
                      </div>
                    )}

                  {activeTab === "hcp" && leftTabData.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                      <div className="text-3xl mb-2">🔍</div>
                      <div className="text-sm font-semibold text-gray-600">
                        No {leftTabLabel} results found
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        No matching records available for this entry.
                      </div>
                    </div>
                  )}
                  {activeTab === "hcp" &&
                    paginatedHcpData.map((hcpRecord) => {
                      const hcpPanelIdMeta = getPanelIdMeta(
                        hcpRecord,
                        "hcp",
                        leftSourceValue,
                      );
                      const isExpandedRow = expandedAccordions.has(
                        hcpRecord.id,
                      );
                      const isSelected =
                        accordionSelected?.[
                          `${record.id}-${leftSelectionType}`
                        ] === hcpRecord.id;
                      return (
                        <div
                          key={hcpRecord.id}
                          className="border border-border rounded-lg overflow-hidden bg-white"
                        >
                          <div
                            onClick={() => toggleAccordion(hcpRecord.id)}
                            className={`flex items-center gap-2 px-3 py-2.5 bg-blue-50 hover:bg-blue-100 cursor-pointer transition-colors ${
                              isSelected ? "ring-2 ring-primary" : ""
                            }`}
                          >
                            <input
                              type="radio"
                              name={`accordion-${leftSelectionType}-${record.id}`}
                              checked={isSelected}
                              onChange={() =>
                                handleRadioSelect(
                                  hcpRecord.id,
                                  leftSelectionType,
                                )
                              }
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 accent-primary cursor-pointer flex-shrink-0"
                            />
                            <div className="flex-1">
                              {hcpRecord.name !== "N/A" && (
                                <div className="text-xs font-semibold text-blue-900">
                                  {hcpRecord.name}
                                </div>
                              )}
                              <div className="text-xs text-blue-700 mt-0.5">
                                {hcpPanelIdMeta.value}
                              </div>
                            </div>
                            {isExpandedRow ? (
                              <KeyboardArrowUp className="w-4 h-4 text-blue-700 flex-shrink-0" />
                            ) : (
                              <KeyboardArrowDown className="w-4 h-4 text-blue-700 flex-shrink-0" />
                            )}
                          </div>
                          {isExpandedRow && (
                            <div className="px-3 py-2 border-t border-border space-y-1">
                              <div className="flex gap-1 text-sm">
                                <span className="text-gray-600 w-28 flex-shrink-0">
                                  {hcpPanelIdMeta.label}:
                                </span>
                                <span className="font-medium text-gray-900 flex-1 text-right">
                                  {hcpPanelIdMeta.value}
                                </span>
                              </div>
                              <div className="flex gap-1 text-sm">
                                <span className="text-gray-600 w-32 flex-shrink-0">
                                  Name:
                                </span>
                                <span className="font-medium text-gray-900 flex-1 text-right">
                                  {hcpRecord.name === hcpPanelIdMeta.value
                                    ? "N/A"
                                    : hcpRecord.name}
                                </span>
                              </div>
                              <div className="flex gap-1 text-sm">
                                <span className="text-gray-600 w-32 flex-shrink-0">
                                  Address:
                                </span>
                                <span className="font-medium text-gray-900 flex-1 text-right">
                                  {getEntityAddress(hcpRecord)}
                                </span>
                              </div>
                              <div className="flex gap-1 text-sm">
                                <span className="text-gray-600 w-32 flex-shrink-0">
                                  City:
                                </span>
                                <span className="font-medium text-gray-900 flex-1 text-right">
                                  {getEntityCity(hcpRecord)}
                                </span>
                              </div>
                              {hcpRecord.status !== "Found in EPH" &&
                                hcpRecord.status !== "Found in Vendor" && (
                                  <div className="flex gap-1 text-sm">
                                    <span className="text-gray-600 w-32 flex-shrink-0">
                                      Status:
                                    </span>
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 ml-auto">
                                      {hcpRecord.status}
                                    </span>
                                  </div>
                                )}
                              <div className="flex gap-1 text-sm">
                                <span className="text-gray-600 w-32 flex-shrink-0">
                                  Specialty:
                                </span>
                                <span className="font-medium text-gray-900 flex-1 text-right">
                                  {hcpRecord.primarySpecialty || "N/A"}
                                </span>
                              </div>
                              <div className="flex gap-1 text-sm">
                                <span className="text-gray-600 w-32 flex-shrink-0">
                                  Classification Code:
                                </span>
                                <span className="font-medium text-gray-900 flex-1 text-right">
                                  {hcpRecord.classificationCode || "N/A"}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  {showRightTab &&
                    activeTab === "hco" &&
                    rightTabData.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-10 text-center">
                        <div className="text-3xl mb-2">🔍</div>
                        <div className="text-sm font-semibold text-gray-600">
                          No {rightTabLabel} results found
                        </div>
                        <div className="text-xs text-gray-400 mt-1">
                          No matching records available for this entry.
                        </div>
                      </div>
                    )}
                  {showRightTab &&
                    activeTab === "hco" &&
                    paginatedHcoData.map((hcoRecord) => {
                      const isSelected =
                        accordionSelected?.[
                          `${record.id}-${rightSelectionType}`
                        ] === hcoRecord.id;
                      return (
                        <div
                          key={hcoRecord.id}
                          className="border border-border rounded-lg overflow-hidden bg-white"
                        >
                          <div
                            onClick={() => toggleAccordion(hcoRecord.id)}
                            className={`flex items-center gap-2 px-3 py-2.5 bg-green-50 hover:bg-green-100 cursor-pointer transition-colors ${
                              isSelected ? "ring-2 ring-primary" : ""
                            }`}
                          >
                            <input
                              type="radio"
                              name={`accordion-${rightSelectionType}-${record.id}`}
                              checked={isSelected}
                              onChange={() =>
                                handleRadioSelect(
                                  hcoRecord.id,
                                  rightSelectionType,
                                )
                              }
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 accent-primary cursor-pointer flex-shrink-0"
                            />
                            <div className="flex-1">
                              {hcoRecord.name !== "N/A" && (
                                <div className="text-xs font-semibold text-green-900">
                                  {hcoRecord.name}
                                </div>
                              )}
                              <div className="text-xs text-green-700 mt-0.5">
                                {hcoRecord.enterpriseId}
                              </div>
                            </div>
                            {expandedAccordions.has(hcoRecord.id) ? (
                              <KeyboardArrowUp className="w-4 h-4 text-green-700 flex-shrink-0" />
                            ) : (
                              <KeyboardArrowDown className="w-4 h-4 text-green-700 flex-shrink-0" />
                            )}
                          </div>
                          {expandedAccordions.has(hcoRecord.id) && (
                            <div className="px-3 py-2 border-t border-border space-y-1">
                              <div className="flex gap-1 text-sm">
                                <span className="text-gray-600 w-32 flex-shrink-0">
                                  {
                                    getPanelIdMeta(
                                      hcoRecord,
                                      rightSelectionType === "parentHco"
                                        ? "hco"
                                        : rightSelectionType,
                                      rightSourceValue,
                                    ).label
                                  }
                                  :
                                </span>
                                <span className="font-medium text-gray-900 flex-1 text-right">
                                  {
                                    getPanelIdMeta(
                                      hcoRecord,
                                      rightSelectionType === "parentHco"
                                        ? "hco"
                                        : rightSelectionType,
                                      rightSourceValue,
                                    ).value
                                  }
                                </span>
                              </div>
                              <div className="flex gap-1 text-sm">
                                <span className="text-gray-600 w-36 flex-shrink-0">
                                  Name:
                                </span>
                                <span className="font-medium text-gray-900 flex-1 text-right">
                                  {hcoRecord.name ===
                                  getPanelIdMeta(
                                    hcoRecord,
                                    rightSelectionType === "parentHco"
                                      ? "hco"
                                      : rightSelectionType,
                                    rightSourceValue,
                                  ).value
                                    ? "N/A"
                                    : hcoRecord.name}
                                </span>
                              </div>
                              <div className="flex gap-1 text-sm">
                                <span className="text-gray-600 w-36 flex-shrink-0">
                                  Address:
                                </span>
                                <span className="font-medium text-gray-900 flex-1 text-right">
                                  {getEntityAddress(hcoRecord)}
                                </span>
                              </div>
                              {hcoRecord.status !== "Found in EPH" &&
                                hcoRecord.status !== "Found in Vendor" && (
                                  <div className="flex gap-1 text-sm">
                                    <span className="text-gray-600 w-36 flex-shrink-0">
                                      Status:
                                    </span>
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 ml-auto">
                                      {hcoRecord.status}
                                    </span>
                                  </div>
                                )}
                              {!isVendorRightPanelData &&
                                !String(hcoRecord?.status || "")
                                  .toLowerCase()
                                  .includes("vendor") && (
                                  <div className="flex gap-1 text-sm">
                                    <span className="text-gray-600 w-36 flex-shrink-0">
                                      Classification Code:
                                    </span>
                                    <span className="font-medium text-gray-900 flex-1 text-right">
                                      {hcoRecord.classificationCode || "N/A"}
                                    </span>
                                  </div>
                                )}
                              <div className="flex gap-1 text-sm">
                                <span className="text-gray-600 w-36 flex-shrink-0">
                                  City:
                                </span>
                                <span className="font-medium text-gray-900 flex-1 text-right">
                                  {getEntityCity(hcoRecord)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                  {/* Pagination Controls */}
                  {activeTab === "hcp" && hcpTotalPages > 1 && (
                    <div className="flex items-center justify-between pt-3 border-t border-border mt-3">
                      <button
                        onClick={() => setHcpPage(Math.max(1, hcpPage - 1))}
                        disabled={hcpPage === 1}
                        className="px-3 py-1 text-xs border border-border rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="text-xs text-gray-600">
                        Page {hcpPage} of {hcpTotalPages}
                      </span>
                      <button
                        onClick={() =>
                          setHcpPage(Math.min(hcpTotalPages, hcpPage + 1))
                        }
                        disabled={hcpPage === hcpTotalPages}
                        className="px-3 py-1 text-xs border border-border rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  )}

                  {showRightTab && activeTab === "hco" && hcoTotalPages > 1 && (
                    <div className="flex items-center justify-between pt-3 border-t border-border mt-3">
                      <button
                        onClick={() => setHcoPage(Math.max(1, hcoPage - 1))}
                        disabled={hcoPage === 1}
                        className="px-3 py-1 text-xs border border-border rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="text-xs text-gray-600">
                        Page {hcoPage} of {hcoTotalPages}
                      </span>
                      <button
                        onClick={() =>
                          setHcoPage(Math.min(hcoTotalPages, hcoPage + 1))
                        }
                        disabled={hcoPage === hcoTotalPages}
                        className="px-3 py-1 text-xs border border-border rounded hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            // Detail view for non-accordion records
            <div className="space-y-4">
              {/* Left Entity Section (HCP or HCO) */}
              <div className="border border-border rounded-lg overflow-hidden">
                <div
                  className={`px-3 py-2 border-b ${
                    isHcoParentLayout || useHcoOnLeft
                      ? "bg-green-50 border-green-200"
                      : "bg-blue-50 border-blue-200"
                  }`}
                >
                  <h3
                    className={`text-xs font-semibold ${
                      isHcoParentLayout || useHcoOnLeft
                        ? "text-green-900"
                        : "text-blue-900"
                    }`}
                  >
                    {leftTabLabel} Details
                  </h3>
                </div>
                <div className="px-3 py-2 space-y-1">
                  {(() => {
                    const leftDetailRecord =
                      effectiveSelectedHcpRecord || effectiveSelectedHcoRecord;
                    return (
                      <>
                        <div className="flex gap-1 text-sm">
                          <span className="text-gray-600 w-28 flex-shrink-0">
                            {leftPanelIdMeta.label}:
                          </span>
                          <span className="font-medium text-gray-900 flex-1 text-right">
                            {leftPanelIdMeta.value}
                          </span>
                        </div>
                        <div className="flex gap-1 text-sm">
                          <span className="text-gray-600 w-28 flex-shrink-0">
                            Name:
                          </span>
                          <span className="font-medium text-gray-900 flex-1 text-right">
                            {effectiveSelectedHcpRecord?.name ||
                              effectiveSelectedHcoRecord?.name ||
                              "N/A"}
                          </span>
                        </div>
                        <div className="flex gap-1 text-sm">
                          <span className="text-gray-600 w-28 flex-shrink-0">
                            Address:
                          </span>
                          <span className="font-medium text-gray-900 flex-1 text-right">
                            {getEntityAddress(leftDetailRecord)}
                          </span>
                        </div>
                        <div className="flex gap-1 text-sm">
                          <span className="text-gray-600 w-28 flex-shrink-0">
                            City:
                          </span>
                          <span className="font-medium text-gray-900 flex-1 text-right">
                            {getEntityCity(leftDetailRecord)}
                          </span>
                        </div>
                        {!useHcoOnLeft && (
                          <>
                            <div className="flex gap-1 text-sm">
                              <span className="text-gray-600 w-28 flex-shrink-0">
                                First Name:
                              </span>
                              <span className="font-medium text-gray-900 flex-1 text-right">
                                {record.details?.firstName || "N/A"}
                              </span>
                            </div>
                            <div className="flex gap-1 text-sm">
                              <span className="text-gray-600 w-28 flex-shrink-0">
                                Last Name:
                              </span>
                              <span className="font-medium text-gray-900 flex-1 text-right">
                                {record.details?.lastName || "N/A"}
                              </span>
                            </div>
                            <div className="flex gap-1 text-sm">
                              <span className="text-gray-600 w-28 flex-shrink-0">
                                Specialty:
                              </span>
                              <span className="font-medium text-gray-900 flex-1 text-right">
                                {effectiveSelectedHcpRecord?.primarySpecialty ||
                                  "N/A"}
                              </span>
                            </div>
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Right Entity Section (HCO or Parent HCO) - only if layout has right tab */}
              {showRightTab && (
                <div className="border border-border rounded-lg overflow-hidden">
                  <div
                    className={`px-3 py-2 border-b ${
                      useParentHcoOnRight
                        ? "bg-purple-50 border-purple-200"
                        : "bg-green-50 border-green-200"
                    }`}
                  >
                    <h3
                      className={`text-xs font-semibold ${
                        useParentHcoOnRight
                          ? "text-purple-900"
                          : "text-green-900"
                      }`}
                    >
                      {rightTabLabel} Details
                    </h3>
                  </div>
                  <div className="px-3 py-2 space-y-1">
                    {(() => {
                      const rightDetailRecord =
                        effectiveSelectedParentHcoRecord ||
                        effectiveSelectedHcoRecord;
                      return (
                        <>
                          <div className="flex gap-1 text-sm">
                            <span className="text-gray-600 w-32 flex-shrink-0">
                              {rightPanelIdMeta.label}:
                            </span>
                            <span className="font-medium text-gray-900 flex-1 text-right">
                              {rightPanelIdMeta.value}
                            </span>
                          </div>
                          <div className="flex gap-1 text-sm">
                            <span className="text-gray-600 w-32 flex-shrink-0">
                              Name:
                            </span>
                            <span className="font-medium text-gray-900 flex-1 text-right">
                              {effectiveSelectedHcoRecord?.name ||
                                effectiveSelectedParentHcoRecord?.name ||
                                "N/A"}
                            </span>
                          </div>
                          <div className="flex gap-1 text-sm">
                            <span className="text-gray-600 w-32 flex-shrink-0">
                              Address:
                            </span>
                            <span className="font-medium text-gray-900 flex-1 text-right">
                              {getEntityAddress(rightDetailRecord)}
                            </span>
                          </div>
                          {!isVendorRightPanelData &&
                            rightSourceValue !== "vendor" && (
                              <div className="flex gap-1 text-sm">
                                <span className="text-gray-600 w-32 flex-shrink-0">
                                  Classification Code:
                                </span>
                                <span className="font-medium text-gray-900 flex-1 text-right">
                                  {effectiveSelectedHcoRecord?.classificationCode ||
                                    effectiveSelectedParentHcoRecord?.classificationCode ||
                                    "N/A"}
                                </span>
                              </div>
                            )}
                          <div className="flex gap-1 text-sm">
                            <span className="text-gray-600 w-32 flex-shrink-0">
                              City:
                            </span>
                            <span className="font-medium text-gray-900 flex-1 text-right">
                              {getEntityCity(rightDetailRecord)}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
